import {
  makeStyles,
  createStyles,
  Typography,
  Grid,
  Collapse,
  Theme,
  useTheme,
  CircularProgress,
  Button,
} from "@material-ui/core";
import { AccountCircle, AddCircleRounded, KeyboardArrowRight } from "@material-ui/icons";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import useCleverlynkFunctions from "../../../hooks/useCleverlynkFunctions";
import useUserFormFunctions from "../../../hooks/useUserFormFunctions";
import { selectCustomerInfo, selectQueryCustomerInfo } from "../../../redux/slices/userFormSlice";
import AddEmail from "./AddEmail";

import { useTranslation } from "react-i18next";
import "../../../i18n/config";
interface IPreviousEmails {
  toggleCollapsed: () => void;
  collapsed: boolean;
  closeDialog: () => void;
  emails: string[];
}

const NEW_MAIL = "__newmail";

const IPreviousEmails = ({ toggleCollapsed, collapsed, closeDialog, emails }: IPreviousEmails) => {
  const [inputNewEmail, setInputNewEmail] = useState<boolean>(false);

  const queryCustomerInfoSupervisor = useSelector(selectQueryCustomerInfo);

  const selectedEmailRef = useRef<string>("");
  const [invalidEmail, setInvalidEmail] = useState<boolean>(false);

  const { handleQueryCustomerInfo, handleModifyIdentification, isValidEmail } = useUserFormFunctions();

  const { t } = useTranslation("common");

  const theme = useTheme();

  useEffect(() => {
    if (queryCustomerInfoSupervisor.status === "succeeded" || queryCustomerInfoSupervisor.status === "failed") {
      closeDialog();
    }
  }, [queryCustomerInfoSupervisor]);

  useEffect(() => {
    setInputNewEmail(collapsed);
  }, [collapsed]);

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      title: {
        fontSize: "1.1rem",
        textAlign: "center",
      },
      emailCard: {
        marginTop: 12,
        height: 40,
        borderRadius: 9,
        boxShadow: "1pt 1pt 3px #0000002e",
        paddingLeft: 12,
        position: "relative",
        cursor: queryCustomerInfoSupervisor.status === "loading" ? "wait" : "pointer",
        "&:hover": {},
        overflow: "hidden",
      },
      addEmailCard: {
        marginTop: 12,
        height: 40,
        borderRadius: 9,
        boxShadow: "1pt 1pt 3px #0000002e",
        paddingLeft: 12,
        position: "relative",
        cursor: "pointer",
        overflow: "hidden",
        transition: "background-color 200ms, height 400ms",
        backgroundColor: theme.palette.primary.main,
        "&.collapsed": {
          height: 50,
        },
        "&.disabled": {
          backgroundColor: "#ccc",
        },
        "&.loading": {
          backgroundColor: theme.palette.secondary.main,
        },
        "&.invalid": {
          backgroundColor: "#fd4067",
        },
      },
      emailText: {
        maxWidth: "100%",
        paddingLeft: 8,
      },
      goArrowDiv: {
        width: 30,
        height: 40,
        boxShadow: "-9pt 0pt 7px white",
        position: "absolute",
        right: 0,
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        paddingRight: 10,
        transitionProperty: "background",
        transitionDuration: "200ms",
        background: theme.palette.secondary.light,
        "&:hover": {},
        "&.loading": {
          background: theme.palette.secondary.contrastText,
        },
        "&.disabled": {
          background: "#ccc",
        },
      },
      addIconDiv: {
        width: "100%",
        height: 40,
        position: "absolute",
        right: 0,
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        paddingRight: 10,
      },
      addRemoveIcon: {
        transition: "transform, 400ms",
        transform: "rotate(0deg)",
        color: theme.palette.primary.contrastText,
        cursor: "pointer",
        "&.collapsed": {
          transform: "rotate(45deg)",
        },
        "&.invalid": {
          color: "white",
        },
      },
      addEmailLabel: {
        color: theme.palette.primary.contrastText,
        paddingLeft: 8,
      },
    })
  );

  const classes = useStyles();

  return (
    <Grid item container style={{ marginTop: 15, marginBottom: 15 }} justifyContent="center">
      <Typography className={classes.title}>{inputNewEmail ? t("inputEmail") : t("selectEmail")}</Typography>
      <Collapse in={!inputNewEmail} style={{ width: "100%" }}>
        {emails.map((e, index) => (
          <Grid
            item
            container
            id={`email-${index}`}
            className={classes.emailCard}
            justifyContent="flex-start"
            alignItems="center"
            onClick={() => {
              if (queryCustomerInfoSupervisor.status !== "loading") {
                selectedEmailRef.current = e;
                handleQueryCustomerInfo(e);
                handleModifyIdentification(e);
              }
            }}>
            <Grid item container wrap="nowrap">
              <AccountCircle style={{ color: theme.palette.primary.main }} />
              <Typography className={classes.emailText}>{e}</Typography>
            </Grid>
            <div
              className={`
              ${queryCustomerInfoSupervisor.status === "loading" && selectedEmailRef.current !== e ? "disabled" : ""} 
              ${queryCustomerInfoSupervisor.status === "loading" && selectedEmailRef.current === e ? "loading" : ""} ${
                classes.goArrowDiv
              }`}>
              {queryCustomerInfoSupervisor.status === "loading" && selectedEmailRef.current === e ? (
                <CircularProgress style={{ color: theme.palette.secondary.main }} size={20} thickness={5} />
              ) : (
                <KeyboardArrowRight style={{ color: theme.palette.secondary.contrastText }} />
              )}
            </div>
          </Grid>
        ))}
      </Collapse>
      <Grid
        item
        container
        className={`
        ${queryCustomerInfoSupervisor.status === "loading" && selectedEmailRef.current !== NEW_MAIL ? "disabled" : ""} 
        ${queryCustomerInfoSupervisor.status === "loading" && selectedEmailRef.current === NEW_MAIL ? "loading" : ""} 
        ${invalidEmail ? "invalid" : ""} 
        ${collapsed ? "collapsed" : ""} ${classes.addEmailCard}`}
        justifyContent="flex-start"
        alignItems="center"
        wrap="nowrap"
        onClick={() => {
          if (!collapsed) {
            toggleCollapsed();
            setInputNewEmail(p => !p);
          }
        }}>
        <AddCircleRounded
          className={`${collapsed ? "collapsed" : ""} ${invalidEmail ? "invalid" : ""} ${classes.addRemoveIcon}`}
          onClick={() => {
            if (collapsed) {
              toggleCollapsed();
              setInputNewEmail(p => !p);
              setInvalidEmail(false);
            }
          }}
        />
        {inputNewEmail ? (
          <AddEmail
            invalid={invalidEmail}
            confirmMail={(id: string) => {
              if (isValidEmail(id)) {
                setInvalidEmail(false);
                selectedEmailRef.current = NEW_MAIL;
                handleQueryCustomerInfo(id);
                handleModifyIdentification(id);
              } else {
                setInvalidEmail(true);
              }
            }}
          />
        ) : (
          <React.Fragment>
            <Grid item container wrap="nowrap">
              <Typography className={classes.addEmailLabel}>{t("addEmail")}</Typography>
            </Grid>
          </React.Fragment>
        )}
      </Grid>
      {collapsed && invalidEmail && <div style={{ color: "#fd4067" }}>{t("pleaseAddEmail")}</div>}
      {collapsed && (
        <Button
          onClick={() => {
            const id: string = (document.getElementById("outlined-input-email") as any).value ?? "";
            // const id = "";
            if (isValidEmail(id)) {
              setInvalidEmail(false);
              selectedEmailRef.current = NEW_MAIL;
              handleQueryCustomerInfo(id);
              handleModifyIdentification(id);
            } else {
              setInvalidEmail(true);
            }
          }}
          variant="contained"
          color="primary"
          style={{
            top: 40,
            left: 80,
            zIndex: 2,
          }}
          endIcon={<KeyboardArrowRight style={{ marginRight: 0 }} 
          id="next"/>}>
          {t("next")}
        </Button>
      )}
    </Grid>
  );
};
export default IPreviousEmails;
